import {
  base_colorContentBorder,
  base_colorRaisedSurface,
  base_colorSurfaceSecondary,
  input_backgroundDefault,
  input_borderActive,
  input_borderDefault,
  input_borderHover,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { transition_fast } from 'ext-ui/style/transitionSpeeds'
import {
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

export const container = css`
  position: relative;
  width: 100%;
`

export const trigger = css`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing(2)};
  padding: ${spacing(2)} ${spacing(3)};
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  ${font_md};
  color: ${text_colorTextPrimary};
  font-family: ${font_family_weight_regular};
  text-align: left;

  &:hover {
    background-color: ${base_colorSurfaceSecondary};
    border-color: ${base_colorContentBorder};
  }

  &:focus-visible {
    outline: 2px solid ${text_colorTextPrimary};
    outline-offset: 2px;
  }
`

// Open state: blue active border mirrors the search field's focus border so
// the trigger and the popover read as one connected control.
export const triggerOpen = css`
  ${trigger};
  border-color: ${input_borderActive};
`

// Boxed variant: a persistent bordered input box at rest, for standalone form
// contexts (e.g. the Assign Account Owner flyout) where the trigger should read
// as a text field rather than the borderless, hover-revealed trigger used in a
// DataGrid cell.
export const triggerBoxed = css`
  ${trigger};
  background: ${input_backgroundDefault};
  border-color: ${input_borderDefault};
  padding: ${spacing(3)} ${spacing(4)};

  &:hover {
    background: ${input_backgroundDefault};
    border-color: ${input_borderHover};
  }
`

// Open state keeps the active border even on hover — triggerBoxed defines its
// own :hover border, so re-override it here or the open trigger would drop back
// to the hover border while the cursor is over it.
export const triggerBoxedOpen = css`
  ${triggerBoxed};
  border-color: ${input_borderActive};

  &:hover {
    border-color: ${input_borderActive};
  }
`

// Muted placeholder text for the boxed variant, so the unselected state reads
// as a form-field placeholder rather than a selected value.
export const triggerPlaceholder = css`
  color: ${text_colorTextSecondary};
`

export const triggerSelected = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacing(2)};
  min-width: 0;
  flex: 1 1 auto;
`

export const triggerSelectedLabel = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

// Holds the "New Invite" pill between the selected label and the chevron;
// never shrinks so the pill stays intact while a long email truncates.
export const triggerPillSlot = css`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
`

export const chevron = css`
  flex: 0 0 auto;
  color: ${text_colorTextSecondary};

  @media (prefers-reduced-motion: no-preference) {
    transition: transform ${transition_fast} ease;
  }
`

export const chevronOpen = css`
  ${chevron};
  transform: rotate(180deg);
`

export const panel = css`
  position: absolute;
  top: calc(100% + ${spacing(1)});
  left: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 360px;
  background-color: ${base_colorRaisedSurface};
  border: 1px solid ${base_colorContentBorder};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`

export const searchInputRow = css`
  padding: ${spacing(2)} ${spacing(3)};
  border-bottom: 1px solid ${base_colorContentBorder};
`

export const searchInputWrapper = css`
  position: relative;
`

export const searchIcon = css`
  position: absolute;
  left: ${spacing(3)};
  top: 50%;
  transform: translateY(-50%);
  color: ${text_colorTextSecondary};
  pointer-events: none;
`

export const searchInput = css`
  width: 100%;
  padding: ${spacing(2)} ${spacing(3)} ${spacing(2)} ${spacing(8)};
  border: 1px solid ${base_colorContentBorder};
  border-radius: 6px;
  background: ${base_colorSurfaceSecondary};
  ${font_md};
  font-family: ${font_family_weight_regular};
  color: ${text_colorTextPrimary};

  &::placeholder {
    color: ${text_colorTextSecondary};
  }

  &:focus-visible {
    outline: none;
    background: transparent;
    border-color: ${input_borderActive};
  }
`

// Grey group label that sets the new-contact result apart, matching the
// figma "New Contact" header.
export const groupHeader = css`
  padding: ${spacing(2)} ${spacing(3)} ${spacing(1)};
  font-family: ${font_family_weight_medium};
  ${font_sm};
  color: ${text_colorTextSecondary};
`

export const listScroll = css`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing(1)} 0;
`

export const optionRow = css`
  appearance: none;
  display: flex;
  width: 100%;
  align-items: center;
  gap: ${spacing(3)};
  padding: ${spacing(2)} ${spacing(3)};
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    background-color: ${base_colorSurfaceSecondary};
  }
`

export const optionTextColumn = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

export const newContactCaption = css`
  font-family: ${font_family_weight_regular};
  ${font_sm};
  color: ${text_colorTextSecondary};
`

export const optionLabel = css`
  font-family: ${font_family_weight_medium};
  ${font_md};
  color: ${text_colorTextPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const optionSublabel = css`
  font-family: ${font_family_weight_regular};
  ${font_sm};
  color: ${text_colorTextSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const emptyState = css`
  padding: ${spacing(4)} ${spacing(3)};
  text-align: center;
  ${font_sm};
  color: ${text_colorTextSecondary};
`

export const loadingState = css`
  padding: ${spacing(3)};
  text-align: center;
  ${font_sm};
  color: ${text_colorTextSecondary};
`
